import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PROSPECTOR_ENGINE_URL } from '../lib/engineUrls';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Highlighter, 
  MessageSquare, 
  Database, 
  Play, 
  Calendar, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Pause, 
  Trash2, 
  RefreshCw,
  Settings,
  Info,
  Loader2,
  ChevronDown,
  ChevronRight,
  Search
} from 'lucide-react';

const DEFAULT_TARGET_DATABASE_ID = '26f435ce6757463da6ea69cc715c2c64';
const DEFAULT_SOURCE_DATABASE_ID = '2958d96385e08167b1eaea7894422cef';

interface HighlightColor {
  id: string;
  name: string;
  color: string;
}

interface ScheduledJob {
  id: string;
  interval: string;
  status: 'running' | 'paused' | 'stopped';
  nextRun?: string;
  lastRun?: string;
  options: {
    sourceDatabaseId: string;
    targetDatabaseId: string;
    highlightColors: string[] | null;
    includeComments: boolean;
    pageLimit: number;
  };
  lastExecution?: {
    lastRun: string;
    result?: {
      pagesScanned: number;
      pagesCreated: number;
      totalHighlights: number;
      totalComments: number;
      errors: number;
    };
    error?: string;
  };
}

interface ExtractionResult {
  success: boolean;
  pagesScanned?: number;
  pagesCreated?: number;
  totalHighlights?: number;
  totalComments?: number;
  pages?: Array<{
    sourcePageTitle?: string;
    pageTitle?: string;
    sourcePageId?: string;
    pageId?: string;
    pageUrl?: string;
    highlightCount: number;
    commentCount: number;
    hasHighlights?: boolean;
    status?: 'created' | 'checked_no_highlights' | 'already_processed';
    targetPageId?: string;
    targetPageUrl?: string;
    existingHighlightPageId?: string;
    existingHighlightPageUrl?: string;
    createdPageId?: string;
  }>;
  errors?: Array<{ pageId: string; error: string }>;
  error?: string;
}

const AVAILABLE_COLORS: HighlightColor[] = [
  { id: 'yellow_background', name: 'Yellow', color: '#FEF9C3' },
  { id: 'blue_background', name: 'Blue', color: '#DBEAFE' },
  { id: 'green_background', name: 'Green', color: '#DCFCE7' },
  { id: 'pink_background', name: 'Pink', color: '#FCE7F3' },
  { id: 'purple_background', name: 'Purple', color: '#F3E8FF' },
  { id: 'red_background', name: 'Red', color: '#FEE2E2' },
  { id: 'orange_background', name: 'Orange', color: '#FFEDD5' },
  { id: 'brown_background', name: 'Brown', color: '#F5D0C5' },
  { id: 'gray_background', name: 'Gray', color: '#F3F4F6' },
];

const INTERVALS = [
  { value: '15m', label: 'Every 15 minutes' },
  { value: '30m', label: 'Every 30 minutes' },
  { value: '1h', label: 'Every hour' },
  { value: '3h', label: 'Every 3 hours' },
  { value: '6h', label: 'Every 6 hours' },
  { value: '12h', label: 'Every 12 hours' },
  { value: '24h', label: 'Daily' },
  { value: '1w', label: 'Weekly' },
  { value: '1mo', label: 'Monthly' },
];

export const NotionHighlights: React.FC = () => {
  // Form state
  const [sourceDatabaseId, setSourceDatabaseId] = useState(DEFAULT_SOURCE_DATABASE_ID);
  const [targetDatabaseId, setTargetDatabaseId] = useState(DEFAULT_TARGET_DATABASE_ID);
  const [selectedColors, setSelectedColors] = useState<string[]>(['yellow_background']);
  const [includeComments, setIncludeComments] = useState(true);
  const [pageLimit, setPageLimit] = useState(25);
  const [jobName, setJobName] = useState('');
  const [interval, setInterval] = useState('24h');
  const [pageUrl, setPageUrl] = useState('');
  const [dateFilterType, setDateFilterType] = useState<'none' | 'created' | 'modified'>('none');
  const [dateFilterTimeframe, setDateFilterTimeframe] = useState<'1d' | '3d' | '1w' | '2w' | '1mo' | '3mo'>('1w');
  
  // UI state
  const [isExtracting, setIsExtracting] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [extractionResult, setExtractionResult] = useState<ExtractionResult | null>(null);
  const [scheduledJobs, setScheduledJobs] = useState<ScheduledJob[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  const [elapsedTime, setElapsedTime] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Update elapsed time while extracting - simple approach
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!isExtracting) {
      setElapsedTime(0);
      return;
    }

    // Start interval that updates every second
    const startTime = Date.now();
    
    // Update immediately
    const updateElapsed = () => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsedTime(elapsed);
    };
    
    // Set initial value
    setElapsedTime(0);
    
    // Update every second
    intervalRef.current = setInterval(() => {
      updateElapsed();
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isExtracting]);

  // Check connection on mount
  useEffect(() => {
    checkConnection();
    loadScheduledJobs();
  }, []);

  const checkConnection = async () => {
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/health`);
      if (response.ok) {
        setIsConnected(true);
      } else {
        setIsConnected(false);
      }
    } catch {
      setIsConnected(false);
    }
  };

  const loadScheduledJobs = useCallback(async () => {
    setIsLoadingJobs(true);
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion-highlights/schedule`);
      if (response.ok) {
        const data = await response.json();
        setScheduledJobs(data.jobs || []);
      }
    } catch (err) {
      console.error('Failed to load scheduled jobs:', err);
    } finally {
      setIsLoadingJobs(false);
    }
  }, []);

  const toggleColor = (colorId: string) => {
    setSelectedColors(prev => 
      prev.includes(colorId) 
        ? prev.filter(c => c !== colorId)
        : [...prev, colorId]
    );
  };

  const handleExtractNow = async () => {
    if (!sourceDatabaseId) {
      setError('Source Database ID is required');
      return;
    }
    
    // Warn if source and target are the same
    const effectiveTargetId = targetDatabaseId || DEFAULT_TARGET_DATABASE_ID;
    if (sourceDatabaseId.replace(/-/g, '') === effectiveTargetId.replace(/-/g, '')) {
      setError('Source and Target database IDs cannot be the same! The source should contain pages WITH highlights, the target is where highlights are SAVED.');
      return;
    }

    setError(null);
    setExtractionResult(null);
    setSuccessMessage(null);
    setIsExtracting(true);

    try {
      // Set a 6-minute timeout (server has 5-minute timeout)
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const timeoutId = setTimeout(() => controller.abort(), 6 * 60 * 1000);
      
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion-highlights/extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceDatabaseId,
          targetDatabaseId: targetDatabaseId || DEFAULT_TARGET_DATABASE_ID,
          highlightColors: selectedColors.length > 0 ? selectedColors : null,
          includeComments,
          pageLimit,
          dateFilter: dateFilterType !== 'none' ? {
            type: dateFilterType,
            timeframe: dateFilterTimeframe
          } : null
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      const data = await response.json();
      console.log('Extraction response:', data);
      
      if (data.success) {
        setExtractionResult(data);
        setSuccessMessage(`Successfully extracted ${data.totalHighlights || 0} highlights and ${data.totalComments || 0} comments from ${data.pagesScanned || 0} pages.`);
      } else {
        console.error('Extraction failed:', data);
        setError(data.error || 'Extraction failed');
        if (data.notionDetails || data.details) {
          console.error('Notion API details:', data.notionDetails || data.details);
        }
      }
    } catch (err: any) {
      console.error('Extraction request failed:', err);
      if (err.name === 'AbortError') {
        setError('Request timed out. The extraction is taking longer than expected. This may be due to rate limiting - please wait a few minutes and try again with a smaller page limit.');
      } else {
        setError(err.message || 'Failed to connect to Prospector Engine');
      }
    } finally {
      setIsExtracting(false);
      abortControllerRef.current = null;
    }
  };

  const handleScheduleJob = async () => {
    if (!sourceDatabaseId) {
      setError('Source Database ID is required');
      return;
    }
    
    // Warn if source and target are the same
    const effectiveTargetId = targetDatabaseId || DEFAULT_TARGET_DATABASE_ID;
    if (sourceDatabaseId.replace(/-/g, '') === effectiveTargetId.replace(/-/g, '')) {
      setError('Source and Target database IDs cannot be the same! The source should contain pages WITH highlights, the target is where highlights are SAVED.');
      return;
    }

    setIsScheduling(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion-highlights/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceDatabaseId,
          targetDatabaseId: targetDatabaseId || DEFAULT_TARGET_DATABASE_ID,
          highlightColors: selectedColors.length > 0 ? selectedColors : null,
          includeComments,
          pageLimit,
          interval,
          jobName: jobName || `highlights-${Date.now()}`
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`Job "${data.jobId}" scheduled successfully! Will run ${INTERVALS.find(i => i.value === interval)?.label.toLowerCase()}.`);
        loadScheduledJobs();
        setJobName('');
      } else {
        setError(data.error || 'Failed to schedule job');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to connect to Prospector Engine');
    } finally {
      setIsScheduling(false);
    }
  };

  const handleRunJob = async (jobId: string) => {
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion-highlights/schedule/${jobId}/run`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`Job "${jobId}" executed successfully!`);
        loadScheduledJobs();
      } else {
        setError(data.error || 'Failed to run job');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to run job');
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    if (!confirm(`Are you sure you want to delete job "${jobId}"?`)) return;

    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion-highlights/schedule/${jobId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`Job "${jobId}" deleted successfully!`);
        loadScheduledJobs();
      } else {
        setError(data.error || 'Failed to delete job');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete job');
    }
  };

  // Extract page ID from Notion URL
  const extractPageIdFromUrl = (url: string): string | null => {
    if (!url) return null;
    
    // Remove trailing slash
    const cleanUrl = url.trim().replace(/\/$/, '');
    
    // Notion URLs: https://www.notion.so/Page-Title-{pageId}
    // The page ID is the last 32 characters (with or without hyphens)
    const match = cleanUrl.match(/([a-f0-9]{8}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[a-f0-9]{12})$/i);
    if (match) {
      return match[1];
    }
    
    // Try without hyphens
    const matchNoHyphens = cleanUrl.match(/([a-f0-9]{32})$/i);
    if (matchNoHyphens) {
      return matchNoHyphens[1];
    }
    
    return null;
  };

  const handleScanPageUrl = async () => {
    if (!pageUrl) {
      setError('Page URL is required');
      return;
    }

    const pageId = extractPageIdFromUrl(pageUrl);
    if (!pageId) {
      setError('Invalid Notion page URL. Please paste a full Notion page URL.');
      return;
    }

    setError(null);
    setExtractionResult(null);
    setSuccessMessage(null);
    setIsExtracting(true);

    try {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const timeoutId = setTimeout(() => controller.abort(), 11 * 60 * 1000); // 11 minutes (backend has 10 minute timeout)
      
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion-highlights/rescan-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourcePageId: pageId,
          targetDatabaseId: targetDatabaseId || DEFAULT_TARGET_DATABASE_ID,
          highlightColors: selectedColors.length > 0 ? selectedColors : null,
          includeComments
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`Successfully scanned page! Found ${data.highlightCount || 0} highlights and ${data.commentCount || 0} comments.`);
        // Show the result
        setExtractionResult({
          success: true,
          pagesScanned: 1,
          pagesCreated: 1,
          totalHighlights: data.highlightCount || 0,
          totalComments: data.commentCount || 0,
          pages: [{
            pageId: pageId,
            pageTitle: data.pageTitle || 'Scanned Page',
            pageUrl: data.pageUrl || pageUrl,
            highlightCount: data.highlightCount || 0,
            commentCount: data.commentCount || 0,
            hasHighlights: (data.highlightCount || 0) > 0,
            status: 'created',
            targetPageId: data.pageId,
            targetPageUrl: data.url
          }]
        });
      } else {
        setError(data.error || 'Failed to scan page');
      }
    } catch (err: any) {
      console.error('Page scan failed:', err);
      if (err.name === 'AbortError') {
        setError('Request timed out. The scan is taking longer than expected.');
      } else {
        setError(err.message || 'Failed to scan page');
      }
    } finally {
      setIsExtracting(false);
      abortControllerRef.current = null;
    }
  };

  const handleCancelExtraction = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsExtracting(false);
      setError('Extraction cancelled by user');
      abortControllerRef.current = null;
    }
  };

  const handleRescanPage = async (pageId: string, pageTitle: string) => {
    if (!confirm(`Rescan "${pageTitle}"? This will update the highlight page with any new highlights.`)) return;

    setIsExtracting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion-highlights/rescan-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourcePageId: pageId,
          targetDatabaseId: targetDatabaseId || DEFAULT_TARGET_DATABASE_ID,
          highlightColors: selectedColors.length > 0 ? selectedColors : null,
          includeComments
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`Successfully rescanned "${pageTitle}". Found ${data.highlightCount || 0} highlights and ${data.commentCount || 0} comments.`);
        // Refresh the extraction results if we have them
        if (extractionResult) {
          // Trigger a new extraction to refresh the list
          // Or just show success message
        }
      } else {
        setError(data.error || 'Failed to rescan page');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to rescan page');
    } finally {
      setIsExtracting(false);
    }
  };

  const toggleJobExpanded = (jobId: string) => {
    setExpandedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div>
        <Link to="/elevation/engines" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Engines
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="h-12 w-12 bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg">
            <Highlighter className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">Notion Highlights Extraction</h1>
            <p className="text-charcoal-600">Prospector Engine Feature</p>
          </div>
        </div>
        <p className="text-lg text-charcoal-700">
          Extract highlighted text and comments from Notion pages and create organized highlight pages in a target database.
        </p>
      </div>

      {/* Connection Status */}
      <div className={`p-4  border-2 ${
        isConnected === null ? 'bg-[var(--surface)] border-[var(--rule)]' :
        isConnected ? 'bg-[var(--green-dim)] border-[var(--green)]' : 'bg-[var(--coral-dim)] border-[var(--coral)]'
      }`}>
        <div className="flex items-center gap-3">
          {isConnected === null ? (
            <Loader2 className="h-5 w-5 text-[var(--text-ter)] animate-spin" />
          ) : isConnected ? (
            <CheckCircle className="h-5 w-5 text-[var(--green)]" />
          ) : (
            <AlertCircle className="h-5 w-5 text-[var(--coral)]" />
          )}
          <div>
            <p className={`font-medium ${
              isConnected === null ? 'text-[var(--text-sec)]' :
              isConnected ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]'
            }`}>
              {isConnected === null ? 'Checking connection...' :
               isConnected ? 'Connected to Prospector Engine (port 4001)' : 
               'Prospector Engine not running'}
            </p>
            {!isConnected && isConnected !== null && (
              <p className="text-sm text-[var(--coral)] mt-1">
                Start the engine with: <code className="bg-[var(--coral-dim)] px-2 py-0.5 rounded">cd engines/prospector-engine && npm start</code>
              </p>
            )}
          </div>
          <button 
            onClick={checkConnection}
            className="ml-auto p-2 hover:bg-[var(--bg-raised)]/50 transition-colors"
            title="Refresh connection status"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-[var(--coral-dim)] border-2 border-[var(--coral)] flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-[var(--coral)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-[var(--coral)]">Error</p>
            <p className="text-[var(--coral)]">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto text-[var(--coral)] hover:text-[var(--coral)]">×</button>
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-[var(--green-dim)] border-2 border-[var(--green)] flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-[var(--green)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-[var(--green)] dark:text-[var(--text-ter)]">Success</p>
            <p className="text-[var(--green)] dark:text-[var(--text-ter)]">{successMessage}</p>
          </div>
          <button onClick={() => setSuccessMessage(null)} className="ml-auto text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]">×</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration Panel */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
          <h2 className="text-xl font-bold text-charcoal-900 mb-4 flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configuration
          </h2>

          <div className="space-y-4">
            {/* Source Database */}
            <div>
              <label className="block text-sm font-medium text-charcoal-700 mb-1">
                Source Database ID <span className="text-[var(--coral)]">*</span> <span className="text-[var(--mustard)] text-xs">(database WITH highlights)</span>
              </label>
              <input
                type="text"
                value={sourceDatabaseId}
                onChange={(e) => setSourceDatabaseId(e.target.value)}
                placeholder="Enter your ChatGPT Conversations database ID..."
                className="w-full px-3 py-2 border-2 border-[var(--rule)] focus:border-consulting-slate-blue-500 focus:ring-1 focus:ring-consulting-slate-blue-500 outline-none"
              />
            </div>

            {/* Target Database */}
            <div>
              <label className="block text-sm font-medium text-charcoal-700 mb-1">
                Target Database ID <span className="text-[var(--green)] dark:text-[var(--text-ter)] text-xs">(where highlights are SAVED)</span>
              </label>
              <input
                type="text"
                value={targetDatabaseId}
                onChange={(e) => setTargetDatabaseId(e.target.value)}
                placeholder={DEFAULT_TARGET_DATABASE_ID}
                className="w-full px-3 py-2 border-2 border-[var(--rule)] focus:border-consulting-slate-blue-500 focus:ring-1 focus:ring-consulting-slate-blue-500 outline-none"
              />
              <p className="text-xs text-charcoal-500 mt-1">
                Defaults to ChatGPT Highlights database
              </p>
            </div>

            {/* Scan Single Page by URL */}
            <div className="border-t-2 border-[var(--rule)] pt-4">
              <label className="block text-sm font-medium text-charcoal-700 mb-2">
                Scan Single Page by URL
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={pageUrl}
                  onChange={(e) => setPageUrl(e.target.value)}
                  placeholder="Paste Notion page URL here..."
                  className="flex-1 px-3 py-2 border-2 border-[var(--rule)] focus:border-consulting-slate-blue-500 focus:ring-1 focus:ring-consulting-slate-blue-500 outline-none"
                />
                <button
                  onClick={handleScanPageUrl}
                  disabled={isExtracting || !pageUrl}
                  className="px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 disabled:bg-[var(--surface)] disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <Search className="h-4 w-4" />
                  Scan Page
                </button>
              </div>
              <p className="text-xs text-charcoal-500 mt-1">
                Paste a full Notion page URL to scan just that page
              </p>
            </div>

            {/* Highlight Colors */}
            <div>
              <label className="block text-sm font-medium text-charcoal-700 mb-2">
                Highlight Colors
              </label>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_COLORS.map(color => (
                  <button
                    key={color.id}
                    onClick={() => toggleColor(color.id)}
                    className={`px-3 py-1.5  border-2 text-sm font-medium transition-all ${
                      selectedColors.includes(color.id)
                        ? 'border-charcoal-800 ring-2 ring-charcoal-300'
                        : 'border-[var(--rule)] hover:border-[var(--rule)]'
                    }`}
                    style={{ backgroundColor: color.color }}
                  >
                    {color.name}
                  </button>
                ))}
              </div>
              <p className="text-xs text-charcoal-500 mt-2">
                {selectedColors.length === 0 
                  ? 'All colors will be captured' 
                  : `Capturing: ${selectedColors.map(c => AVAILABLE_COLORS.find(ac => ac.id === c)?.name).join(', ')}`}
              </p>
            </div>

            {/* Include Comments */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="includeComments"
                checked={includeComments}
                onChange={(e) => setIncludeComments(e.target.checked)}
                className="w-5 h-5 text-consulting-slate-blue-600 rounded border-[var(--rule)] focus:ring-consulting-slate-blue-500"
              />
              <label htmlFor="includeComments" className="flex items-center gap-2 text-charcoal-700">
                <MessageSquare className="h-4 w-4" />
                Include Comments
              </label>
            </div>

            {/* Advanced Options */}
            <div>
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm text-charcoal-600 hover:text-charcoal-800"
              >
                {showAdvanced ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                Advanced Options
              </button>
              
              {showAdvanced && (
                <div className="mt-3 pl-6 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-1">
                      Page Limit
                    </label>
                    <input
                      type="number"
                      value={pageLimit}
                      onChange={(e) => setPageLimit(parseInt(e.target.value) || 100)}
                      min={1}
                      max={1000}
                      className="w-32 px-3 py-2 border-2 border-[var(--rule)] focus:border-consulting-slate-blue-500 focus:ring-1 focus:ring-consulting-slate-blue-500 outline-none"
                    />
                    <p className="text-xs text-charcoal-500 mt-1">
                      Maximum pages to scan per extraction
                    </p>
                  </div>

                  {/* Date Filter */}
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Filter by Date
                    </label>
                    <div className="space-y-2">
                      <select
                        value={dateFilterType}
                        onChange={(e) => setDateFilterType(e.target.value as 'none' | 'created' | 'modified')}
                        className="w-full px-3 py-2 border-2 border-[var(--rule)] focus:border-consulting-slate-blue-500 focus:ring-1 focus:ring-consulting-slate-blue-500 outline-none"
                      >
                        <option value="none">No filter</option>
                        <option value="created">Created in timeframe</option>
                        <option value="modified">Modified in timeframe</option>
                      </select>
                      
                      {dateFilterType !== 'none' && (
                        <select
                          value={dateFilterTimeframe}
                          onChange={(e) => setDateFilterTimeframe(e.target.value as '1d' | '3d' | '1w' | '2w' | '1mo' | '3mo')}
                          className="w-full px-3 py-2 border-2 border-[var(--rule)] focus:border-consulting-slate-blue-500 focus:ring-1 focus:ring-consulting-slate-blue-500 outline-none"
                        >
                          <option value="1d">Last 24 hours</option>
                          <option value="3d">Last 3 days</option>
                          <option value="1w">Last week</option>
                          <option value="2w">Last 2 weeks</option>
                          <option value="1mo">Last month</option>
                          <option value="3mo">Last 3 months</option>
                        </select>
                      )}
                    </div>
                    <p className="text-xs text-charcoal-500 mt-1">
                      {dateFilterType === 'none' 
                        ? 'Scan all pages' 
                        : `Only scan pages ${dateFilterType === 'created' ? 'created' : 'modified'} in the selected timeframe`}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 pt-6 border-t border-[var(--rule)] space-y-3">
            <button
              onClick={handleExtractNow}
              disabled={!isConnected || isExtracting || !sourceDatabaseId}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-consulting-slate-blue-600 text-white font-medium hover:bg-consulting-slate-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isExtracting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Extracting... ({elapsedTime}s)</span>
                </>
              ) : (
                <>
                  <Play className="h-5 w-5" />
                  Extract Now
                </>
              )}
            </button>

            {/* Extraction Status Message */}
            {isExtracting && (
              <div className="p-4 bg-[var(--slate-dim)] border-2 border-[var(--slate)]">
                <div className="flex items-start gap-3">
                  <Loader2 className="h-5 w-5 text-[var(--text-ter)] animate-spin flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-[var(--text-sec)]">Processing extraction...</p>
                    <p className="text-sm text-[var(--text-sec)] mt-1">
                      Scanning pages for highlights and comments. This may take 30-60 seconds per page.
                      <br />
                      <span className="font-mono font-bold">Elapsed time: {elapsedTime} seconds</span>
                    </p>
                    <p className="text-xs text-[var(--text-sec)] mt-2">
                      Large pages with many blocks can take longer. Please wait...
                    </p>
                    <button
                      onClick={handleCancelExtraction}
                      className="mt-3 px-4 py-2 bg-[var(--coral)] text-white text-sm hover:bg-[var(--coral)] transition-colors"
                    >
                      Cancel Extraction
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <input
                type="text"
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
                placeholder="Job name (optional)"
                className="flex-1 px-3 py-2 border-2 border-[var(--rule)] focus:border-consulting-slate-blue-500 focus:ring-1 focus:ring-consulting-slate-blue-500 outline-none text-sm"
              />
              <select
                value={interval}
                onChange={(e) => setInterval(e.target.value)}
                className="px-3 py-2 border-2 border-[var(--rule)] focus:border-consulting-slate-blue-500 focus:ring-1 focus:ring-consulting-slate-blue-500 outline-none text-sm"
              >
                {INTERVALS.map(i => (
                  <option key={i.value} value={i.value}>{i.label}</option>
                ))}
              </select>
            </div>

            <button
              onClick={handleScheduleJob}
              disabled={!isConnected || isScheduling || !sourceDatabaseId}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[var(--mustard)] text-white font-medium hover:bg-[var(--mustard)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isScheduling ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Scheduling...
                </>
              ) : (
                <>
                  <Calendar className="h-5 w-5" />
                  Schedule Recurring Job
                </>
              )}
            </button>
          </div>
        </div>

        {/* Scheduled Jobs Panel */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-charcoal-900 flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Scheduled Jobs
            </h2>
            <button 
              onClick={loadScheduledJobs}
              disabled={isLoadingJobs}
              className="p-2 hover:bg-[var(--hover-tint)] transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingJobs ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {scheduledJobs.length === 0 ? (
            <div className="text-center py-8 text-charcoal-500">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No scheduled jobs yet.</p>
              <p className="text-sm mt-1">Create a scheduled job using the form.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {scheduledJobs.map(job => (
                <div 
                  key={job.id}
                  className="border-2 border-[var(--rule)] overflow-hidden"
                >
                  <button
                    onClick={() => toggleJobExpanded(job.id)}
                    className="w-full p-4 flex items-center justify-between hover:bg-[var(--surface)] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        job.status === 'running' ? 'bg-[var(--green)]' :
                        job.status === 'paused' ? 'bg-[var(--mustard)]' : 'bg-[var(--surface)]'
                      }`} />
                      <div className="text-left">
                        <p className="font-medium text-charcoal-900">{job.id}</p>
                        <p className="text-sm text-charcoal-500">
                          {INTERVALS.find(i => i.value === job.interval)?.label || job.interval}
                        </p>
                      </div>
                    </div>
                    {expandedJobs.has(job.id) ? (
                      <ChevronDown className="h-5 w-5 text-charcoal-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-charcoal-400" />
                    )}
                  </button>

                  {expandedJobs.has(job.id) && (
                    <div className="px-4 pb-4 pt-2 border-t border-[var(--rule)] bg-[var(--surface)]">
                      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                        <div>
                          <p className="text-charcoal-500">Source DB</p>
                          <p className="font-mono text-xs truncate">{job.options.sourceDatabaseId}</p>
                        </div>
                        <div>
                          <p className="text-charcoal-500">Target DB</p>
                          <p className="font-mono text-xs truncate">{job.options.targetDatabaseId}</p>
                        </div>
                        <div>
                          <p className="text-charcoal-500">Page Limit</p>
                          <p>{job.options.pageLimit}</p>
                        </div>
                        <div>
                          <p className="text-charcoal-500">Comments</p>
                          <p>{job.options.includeComments ? 'Included' : 'Excluded'}</p>
                        </div>
                      </div>

                      {job.lastExecution && (
                        <div className="mb-4 p-3 bg-[var(--bg-raised)] border border-[var(--rule)]">
                          <p className="text-sm font-medium text-charcoal-700 mb-1">Last Execution</p>
                          <p className="text-xs text-charcoal-500">
                            {new Date(job.lastExecution.lastRun).toLocaleString()}
                          </p>
                          {job.lastExecution.result && (
                            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                              <p>Pages: {job.lastExecution.result.pagesScanned}</p>
                              <p>Created: {job.lastExecution.result.pagesCreated}</p>
                              <p>Highlights: {job.lastExecution.result.totalHighlights}</p>
                              <p>Comments: {job.lastExecution.result.totalComments}</p>
                            </div>
                          )}
                          {job.lastExecution.error && (
                            <p className="text-xs text-[var(--coral)] mt-2">{job.lastExecution.error}</p>
                          )}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleRunJob(job.id)}
                          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-consulting-slate-blue-600 text-white text-sm hover:bg-consulting-slate-blue-700 transition-colors"
                        >
                          <Play className="h-4 w-4" />
                          Run Now
                        </button>
                        <button
                          onClick={() => handleDeleteJob(job.id)}
                          className="px-3 py-2 bg-[var(--coral-dim)] text-[var(--coral)] text-sm hover:bg-[var(--coral-dim)] transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Extraction Results */}
      {extractionResult && (
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
          <h2 className="text-xl font-bold text-charcoal-900 mb-4 flex items-center gap-2">
            <Database className="h-5 w-5" />
            Extraction Results
          </h2>

          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-consulting-slate-blue-50 p-4 text-center">
              <p className="text-3xl font-bold text-consulting-slate-blue-600">{extractionResult.pagesScanned || 0}</p>
              <p className="text-sm text-charcoal-600">Pages Scanned</p>
            </div>
            <div className="bg-[var(--green-dim)] p-4 text-center">
              <p className="text-3xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{extractionResult.pagesCreated || 0}</p>
              <p className="text-sm text-charcoal-600">Pages Created</p>
            </div>
            <div className="bg-[var(--mustard-dim)] p-4 text-center">
              <p className="text-3xl font-bold text-[var(--mustard)]">{extractionResult.totalHighlights || 0}</p>
              <p className="text-sm text-charcoal-600">Highlights</p>
            </div>
            <div className="bg-[var(--slate-dim)] p-4 text-center">
              <p className="text-3xl font-bold text-[var(--text-sec)]">{extractionResult.totalComments || 0}</p>
              <p className="text-sm text-charcoal-600">Comments</p>
            </div>
          </div>

          {extractionResult.pages && extractionResult.pages.length > 0 && (
            <div>
              <h3 className="font-medium text-charcoal-800 mb-3">
                Pages Checked ({extractionResult.pages.length})
              </h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {extractionResult.pages.map((page, idx) => {
                  const pageTitle = page.sourcePageTitle || page.pageTitle || `Page ${idx + 1}`;
                  const hasHighlights = page.hasHighlights || (page.highlightCount && page.highlightCount > 0);
                  const status = page.status || (hasHighlights ? 'created' : 'checked_no_highlights');
                  
                  return (
                    <div 
                      key={page.pageId || idx} 
                      className={`flex items-center justify-between p-3  border ${
                        hasHighlights 
                          ? 'bg-[var(--green-dim)] border-[var(--green)]' 
                          : 'bg-[var(--surface)] border-[var(--rule)]'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-charcoal-900 truncate">{pageTitle}</p>
                          {hasHighlights ? (
                            <span className="px-2 py-0.5 text-xs font-medium bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] rounded">
                              {page.highlightCount || 0} highlights
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs font-medium bg-[var(--hover-tint)] text-[var(--text-sec)] rounded">
                              No highlights
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-charcoal-500 mt-1">
                          {page.commentCount ? `${page.commentCount} comments` : 'No comments'}
                          <span className="mx-2">•</span>
                          {page.pageUrl && (
                            <a 
                              href={page.pageUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-consulting-slate-blue-600 hover:underline"
                            >
                              Source →
                            </a>
                          )}
                          {(page.targetPageUrl || page.existingHighlightPageUrl) && (
                            <>
                              <span className="mx-2">•</span>
                              <a 
                                href={page.targetPageUrl || page.existingHighlightPageUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[var(--green)] dark:text-[var(--text-ter)] hover:underline font-medium"
                              >
                                View Highlights →
                              </a>
                            </>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        {status === 'already_processed' && (
                          <button
                            onClick={() => handleRescanPage(page.pageId || page.sourcePageId || '', pageTitle)}
                            className="px-3 py-1.5 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs hover:bg-[var(--mustard-dim)] transition-colors flex items-center gap-1"
                            title="Rescan this page"
                          >
                            <RefreshCw className="h-3 w-3" />
                            Rescan
                          </button>
                        )}
                        {hasHighlights && (page.targetPageUrl || page.existingHighlightPageUrl) ? (
                          <a
                            href={page.targetPageUrl || page.existingHighlightPageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]"
                            title="View created highlight page"
                          >
                            <CheckCircle className="h-5 w-5" />
                          </a>
                        ) : hasHighlights ? (
                          <CheckCircle className="h-5 w-5 text-[var(--green)]" />
                        ) : status === 'already_processed' ? (
                          <span className="text-[var(--mustard)] text-xs">Already processed</span>
                        ) : (
                          <span className="text-[var(--text-ter)] text-xs">✓</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {extractionResult.errors && extractionResult.errors.length > 0 && (
            <div className="mt-4 p-4 bg-[var(--coral-dim)]">
              <h3 className="font-medium text-[var(--coral)] mb-2">Errors ({extractionResult.errors.length})</h3>
              <div className="space-y-1 text-sm text-[var(--coral)]">
                {extractionResult.errors.slice(0, 5).map((err, idx) => (
                  <p key={idx}>{err.error}</p>
                ))}
                {extractionResult.errors.length > 5 && (
                  <p className="text-[var(--coral)]">...and {extractionResult.errors.length - 5} more</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info Panel */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-amber-50 p-6 border-2 border-consulting-slate-blue-200">
        <div className="flex items-start gap-4">
          <Info className="h-6 w-6 text-consulting-slate-blue-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-bold text-charcoal-900 mb-2">Target Database Schema</h3>
            <p className="text-charcoal-700 mb-3">
              The ChatGPT Highlights database uses the following structure:
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-[var(--bg-raised)]/50 p-3">
                <p className="font-medium text-charcoal-800">Highlight Title</p>
                <p className="text-charcoal-600">Name/title from source page</p>
              </div>
              <div className="bg-[var(--bg-raised)]/50 p-3">
                <p className="font-medium text-charcoal-800">Highlighted Text</p>
                <p className="text-charcoal-600">The actual highlighted content</p>
              </div>
              <div className="bg-[var(--bg-raised)]/50 p-3">
                <p className="font-medium text-charcoal-800">Source Conversation</p>
                <p className="text-charcoal-600">Relation to original page</p>
              </div>
              <div className="bg-[var(--bg-raised)]/50 p-3">
                <p className="font-medium text-charcoal-800">Category</p>
                <p className="text-charcoal-600">Key Insight, Action Item, Quote, Question, Concept</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotionHighlights;

